/**
 * AiReviewBatchDetailDrawer — 批量 AI 审查批详情（波次 2）。
 *
 * 提交受理（202）后的进度回看：进度条「X/N 已完成，Y 失败」+ 行级表格
 * （文档 / 状态徽标 / 发现数 / 失败原因 / 操作）。行级失败呈现参照
 * ExtractionGrid 的口径——失败行可见原因、成功行给出可核对的数字，跳过行
 * 原样标注不与成功混排。重试仅 failed 且 canRetry 的行可用（按钮渲染在
 * 每一行上，非 failed 行禁用，状态一目了然）。批未终态时由
 * useAiReviewBatch 自动轮询。
 */

import { App, Button, Drawer, Progress, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useAiReviewBatch, useRetryAiReviewBatchItem } from "../../api/queries";
import { mapApiError } from "../../utils/api-error";
import {
  AI_REVIEW_BATCH_ITEM_STATUS_LABELS,
  type AiReviewBatchItem,
} from "../../types/aiReviewBatch";
import { BATCH_ITEM_STATUS_TAG_COLOR } from "./constants";
import EmptyState from "../common/EmptyState";
import { SkeletonGrid } from "../common/Skeleton";

const { Text } = Typography;

/** 终态行：不在 pending/running 中的行计入「已完成」。 */
const FINISHED_ITEM_STATUSES = new Set(["succeeded", "failed", "skipped"]);

interface AiReviewBatchDetailDrawerProps {
  batchId: string | null;
  open: boolean;
  onClose: () => void;
}

export default function AiReviewBatchDetailDrawer({
  batchId,
  open,
  onClose,
}: AiReviewBatchDetailDrawerProps) {
  const { message } = App.useApp();
  const batchQuery = useAiReviewBatch(open ? batchId : null);
  const batch = batchQuery.data;
  const retryMut = useRetryAiReviewBatchItem();

  const items = batch?.items ?? [];
  const total = items.length;
  const finished = items.filter((i) => FINISHED_ITEM_STATUSES.has(i.status)).length;
  const failed = items.filter((i) => i.status === "failed").length;
  const percent = total === 0 ? 0 : Math.round((finished / total) * 100);

  const handleRetry = async (item: AiReviewBatchItem) => {
    if (!batchId || !item.canRetry || item.status !== "failed") return;
    try {
      await retryMut.mutateAsync({ batchId, itemId: item.id });
      message.success(`已重新排队：${item.documentName}`);
    } catch (err) {
      message.error(mapApiError(err));
    }
  };

  const columns: ColumnsType<AiReviewBatchItem> = [
    {
      title: "文档",
      dataIndex: "documentName",
      key: "documentName",
      ellipsis: true,
      render: (_, item) => (
        <span data-testid={`batch-item-doc-${item.id}`}>{item.documentName}</span>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (_, item) => (
        <Tag color={BATCH_ITEM_STATUS_TAG_COLOR[item.status]}>
          {AI_REVIEW_BATCH_ITEM_STATUS_LABELS[item.status]}
        </Tag>
      ),
    },
    {
      title: "发现数",
      dataIndex: "findingCount",
      key: "findingCount",
      width: 90,
      render: (_, item) =>
        item.status === "succeeded" ? (item.findingCount ?? 0) : <Text type="secondary">—</Text>,
    },
    {
      title: "失败原因",
      dataIndex: "failureReason",
      key: "failureReason",
      ellipsis: true,
      render: (_, item) =>
        item.status === "failed" && item.failureReason ? (
          <Tooltip title={item.failureReason}>
            <Text type="danger" ellipsis style={{ maxWidth: 240 }}>
              {item.failureReason}
            </Text>
          </Tooltip>
        ) : null,
    },
    {
      title: "操作",
      key: "actions",
      width: 90,
      render: (_, item) => (
        <Button
          size="small"
          type="link"
          disabled={item.status !== "failed" || !item.canRetry}
          loading={
            retryMut.isPending &&
            retryMut.variables?.itemId === item.id
          }
          onClick={() => void handleRetry(item)}
          data-testid={`batch-item-retry-${item.id}`}
        >
          重试
        </Button>
      ),
    },
  ];

  return (
    <Drawer
      title={batch?.title ?? "批量审查详情"}
      open={open}
      onClose={onClose}
      width={720}
      destroyOnHidden
      data-testid="batch-detail-drawer"
    >
      {batchQuery.isLoading ? (
        <SkeletonGrid count={5} />
      ) : batchQuery.isError || !batch ? (
        <EmptyState
          title="加载失败"
          description="无法获取该批量审查的详情，请稍后重试。"
          action={
            <Button type="primary" onClick={() => batchQuery.refetch()}>
              重试
            </Button>
          }
        />
      ) : (
        <div>
          <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: "var(--jerry-sp-3)" }}>
            <Text>
              <span data-testid="batch-progress-text">
                {finished}/{total} 已完成，{failed} 失败
              </span>
              {batch.stance && (
                <Text type="secondary"> · 立场：{batch.stance}</Text>
              )}
            </Text>
            <Progress
              percent={percent}
              status={failed > 0 ? "exception" : "active"}
              size="small"
              data-testid="batch-progress-bar"
            />
          </Space>
          <Table<AiReviewBatchItem>
            rowKey="id"
            columns={columns}
            dataSource={items}
            pagination={false}
            size="small"
            rowClassName={(item) => (item.status === "failed" ? "jerry-extraction-row-failed" : "")}
            data-testid="batch-items-table"
          />
        </div>
      )}
    </Drawer>
  );
}
